import React from "react";
import { useToast } from "../../context/ToastContext";
import { BFS_MAPPING } from "./WizardConstants";
import {
  TAX_BRACKET_OPTIONS,
  TAX_BRACKET_MAP,
  PRIMARY_OBJECTIVE_OPTIONS,
  RISK_TOLERANCE_OPTIONS,
  TIME_HORIZON_OPTIONS,
  DECISION_MAKING_OPTIONS,
  DEFAULT_DECISION_MAKING_OPTION,
  SOURCE_OF_FUNDS_OPTIONS,
  PRIMARY_USE_OPTIONS,
  parseAccountRiskTimeHorizonDisplay,
  getAccountRiskTimeHorizonLabel,
} from "../../constants/householdConstants";
import {
  LIQUIDITY_NEEDS_OPTIONS,
  LIQUIDITY_TIME_HORIZON_OPTIONS,
  parseLiquidityNeedsDisplay,
  parseLiquidityTimeHorizonDisplay,
  getLiquidityNeedsLabel,
  getLiquidityTimeHorizonLabel,
} from "../../constants/accountConstants";
import { formatNumericInput } from "../../utils/validationHelpers";
import {
  getBrokerageFlags,
  showAccountDecisionMaking,
  showTaxBracket,
  showAnnualExpenseRange,
  showSpecialExpenseRange,
  showSourceOfFunds,
  showUseOfFunds,
  showLiquidityRequirements,
  showIncomeDetails,
  showStrategyDefaults,
  filterExperienceFields,
} from "../../utils/brokerageFields";

// Smart Range Input Component
const RangeInput = ({ label, value, onChange, prefix = "$" }) => {
    // value is { start, end } or null/undefined
    const { toast } = useToast();
    const minVal = value?.start || "";
    const maxVal = value?.end || "";

    const handleMinChange = (e) => {
        onChange({ ...value, start: formatNumericInput(e.target.value) });
    };

    const handleMaxChange = (e) => {
        onChange({ ...value, end: formatNumericInput(e.target.value) });
    };

    const handleBlur = (field) => {
        const start = value?.start === '' || value?.start === null ? 0 : parseFloat(value.start);
        const end = value?.end === '' || value?.end === null ? 0 : parseFloat(value.end);

        if (value?.start !== '' && value?.start !== null && value?.end !== '' && value?.end !== null && start > end) {
            if (field === 'start') {
                toast.error(`Invalid range for ${label}: 'From' cannot exceed 'To'. The field has been reset.`);
                onChange({ ...value, start: null });
            } else {
                toast.error(`Invalid range for ${label}: 'To' cannot be less than 'From'. The field has been reset.`);
                onChange({ ...value, end: null });
            }
        }
    };

    return (
        <div>
            <label className="label-modern">{label}</label>
            <div className="flex items-center gap-3">
                <div className="relative flex-1">
                    {prefix && <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 font-medium">{prefix}</span>}
                    <input 
                        type="number" 
                        min="0"
                        placeholder="Min"
                        className={`input-modern w-full text-right no-spinner ${prefix ? 'pl-7' : 'pl-3'}`}
                        value={minVal}
                        onChange={handleMinChange}
                        onBlur={() => handleBlur('start')}
                        onKeyDown={(e) => {
                            if (e.key === '-') e.preventDefault();
                            if (e.key === 'Enter') handleBlur('start');
                        }}
                        onWheel={(e) => e.currentTarget.blur()}
                    />
                </div>
                <div className="text-gray-400 font-medium">–</div>
                <div className="relative flex-1">
                   {prefix && <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 font-medium">{prefix}</span>}
                    <input 
                        type="number" 
                        min="0"
                        placeholder="Max"
                        className={`input-modern w-full text-right no-spinner ${prefix ? 'pl-7' : 'pl-3'}`}
                        value={maxVal}
                        onChange={handleMaxChange}
                        onBlur={() => handleBlur('end')}
                        onKeyDown={(e) => {
                            if (e.key === '-') e.preventDefault();
                            if (e.key === 'Enter') handleBlur('end');
                        }}
                        onWheel={(e) => e.currentTarget.blur()}
                    />
                </div>
            </div>
        </div>
    );
};

// Collapsible Section Component
const CollapsibleSection = ({ index, title, isOpen, onToggle, children }) => {
    return (
        <div className="bg-white border border-gray-200 rounded-xl mb-4 shadow-sm hover:shadow-md transition-all overflow-hidden">
            <button 
                type="button"
                onClick={(e) => { e.preventDefault(); onToggle(); }}
                className="w-full flex items-center justify-between p-5 cursor-pointer bg-gray-50/50 hover:bg-gray-100 transition-colors text-left focus:outline-none"
            >
                <h3 className="section-title text-lg font-bold text-gray-800 flex items-center gap-3">
                    <span className="w-8 h-8 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center text-sm font-bold">{index + 1}</span>
                    {title}
                </h3>
                <div className={`p-2 text-gray-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}>
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                </div>
            </button>
            
            {isOpen && (
                <div className="p-6 border-t border-gray-100 bg-white animate-fadeIn">
                    {children}
                </div>
            )}
        </div>
    );
};

const Step2HouseholdSetup = ({ data, updateData, onNext, onBack }) => {
  const brokerageFlags = getBrokerageFlags(data);
  const showIncomeOverviewCard =
      showTaxBracket(brokerageFlags) ||
      showIncomeDetails(brokerageFlags, data?.accounts) ||
      showAnnualExpenseRange(brokerageFlags) ||
      showSpecialExpenseRange(brokerageFlags);
  const incomeCardOffset = showIncomeOverviewCard ? 0 : -1;
  const experienceFieldsToRender = filterExperienceFields([
      { key: 'stocks', label: 'Stocks' },
      { key: 'bonds', label: 'Bonds' },
      { key: 'mutual_funds', label: 'Mutual Funds' },
      { key: 'alternative_investments', label: 'Alternative Investments' },
      { key: 'variable_annuities', label: 'V Annuities' },
      { key: 'partnerships', label: 'Partnerships' },
      { key: 'options', label: 'Options' },
  ], brokerageFlags, data?.accounts);

  // State for collapsible cards
  const [expandedCards, setExpandedCards] = React.useState({
      0: true, // Income Overview
      1: true, // Regulatory Qualifiers
      2: true, // Investment Experience
      3: true, // Investment Strategy
  });

  const toggleCard = (index) => {
      setExpandedCards(prev => ({
          ...prev,
          [index]: !prev[index]
      }));
  };

  // Helper: Parse tax bracket display value to {start, end}
  const parseTaxBracket = (displayValue) => {
      if (!displayValue) return null;
      return TAX_BRACKET_MAP[displayValue] || null;
  };

  // Helper: Format tax bracket from {start, end} to display
  const formatTaxBracket = (taxBracket) => {
      if (!taxBracket || taxBracket.start === null || taxBracket.start === undefined) return "";
      const entry = Object.entries(TAX_BRACKET_MAP).find(
          ([, v]) => v.start === taxBracket.start && v.end === taxBracket.end
      );
      return entry ? entry[0] : "";
  };

  const parseTimeHorizon = parseAccountRiskTimeHorizonDisplay;

  const formatTimeHorizon = (timeHorizon) => getAccountRiskTimeHorizonLabel(timeHorizon, "");

  const parseLiquidityNeeds = (displayValue) => {
      return parseLiquidityNeedsDisplay(displayValue);
  };

  const formatLiquidityNeeds = (liquidityReq) => {
      return getLiquidityNeedsLabel(liquidityReq?.amount, '');
  };

  const parseLiquidityTimeHorizon = (displayValue) => {
      return parseLiquidityTimeHorizonDisplay(displayValue);
  };

  const formatLiquidityTimeHorizon = (liquidityReq) => {
      return getLiquidityTimeHorizonLabel(liquidityReq?.time_horizon, '');
  };

  // Helpers
  const handleFinancialChange = (field, value) => {
      let sanitizedValue = value;
      if (typeof value === "string") {
          sanitizedValue = formatNumericInput(value);
      }
      updateData({ [field]: sanitizedValue });
  };
  
  const handleTaxBracketChange = (displayValue) => {
      const taxBracket = parseTaxBracket(displayValue);
      updateData({
          tax_info: { ...(data.tax_info || {}), tax_bracket: taxBracket }
      });
  };

  const handleInvestorAssociationChange = (key) => {
      const currentValue = data.investor_associations?.[key];
      // Treat null/undefined as false
      const newValue = currentValue ? false : true;
      updateData({
          investor_associations: {
              ...(data.investor_associations || {}),
              [key]: newValue
          }
      });
  };

  const handleInvestorExperienceChange = (key, value) => {
      let sanitizedValue = formatNumericInput(value);
      if (sanitizedValue !== "") {
          const numVal = parseInt(sanitizedValue);
          if (numVal > 100) sanitizedValue = "";
      }

      updateData({
          investor_experience: {
              ...(data.investor_experience || {}),
              [key]: sanitizedValue,
          },
      });
  };

  const setStrategy = (newStrategy) => {
      updateData({
          investment_strategy_defaults: {
              ...(data.investment_strategy_defaults || {}),
              ...newStrategy
          }
      });
  };

  const handleTimeHorizonChange = (displayValue) => {
      const timeHorizon = parseTimeHorizon(displayValue);
      setStrategy({ time_horizon: timeHorizon });
  };

  const handleLiquidityNeedsChange = (displayValue) => {
      const amount = parseLiquidityNeeds(displayValue);
      setStrategy({
          liquidity_requirements: {
              ...(strategy.liquidity_requirements || {}),
              amount
          }
      });
  };

  const handleLiquidityTimeHorizonChange = (displayValue) => {
      const time_horizon = parseLiquidityTimeHorizon(displayValue);
      setStrategy({
          liquidity_requirements: {
              ...(strategy.liquidity_requirements || {}),
              time_horizon
          }
      });
  };
  
  // Handler for Primary Objective Change with Auto-Fill Logic
  const handlePrimaryObjectiveChange = (value) => {
       // 1. Update the objective itself (wrapping in array)
       const newStrategy = { invest_objectives: [value] };

       // 2. Look up "BFS Mapping" based on this objective
       const matchingKey = Object.keys(BFS_MAPPING).find(key => BFS_MAPPING[key].invest_objectives?.[0] === value);
       
       if (matchingKey) {
           const defaults = BFS_MAPPING[matchingKey];
           Object.assign(newStrategy, defaults);
           if (typeof newStrategy.time_horizon === 'string') {
               newStrategy.time_horizon = parseTimeHorizon(newStrategy.time_horizon);
           }
       }
       
       setStrategy(newStrategy);
  };


  // Safe Accessors
  const strategy = data.investment_strategy_defaults || {};
  
  // Normalize associations: treat null/undefined as false
  const rawAssociations = data.investor_associations || {};
  const associations = {
      accredited_investor: rawAssociations.accredited_investor || false,
      qualified_purchaser: rawAssociations.qualified_purchaser || false,
      us_broker_dealer_association: rawAssociations.us_broker_dealer_association || false,
      broker_dealer_employee: rawAssociations.broker_dealer_employee || false,
      ria_association: rawAssociations.ria_association || false,
      shareholder_or_member_board_director: rawAssociations.shareholder_or_member_board_director || false,
      low_priced_securities: rawAssociations.low_priced_securities || false,
  };
  
  const experience = data.investor_experience || {};
  const taxInfo = data.tax_info || {};

  // Initialize default Source of Funds if missing (only when the field applies)
  React.useEffect(() => {
    if (showSourceOfFunds(brokerageFlags) && (!strategy.source_of_funds || strategy.source_of_funds.length === 0)) {
        setStrategy({ source_of_funds: ['Income'] });
    }
  }, []);

  // Initialize default Account Decision Making if missing
  React.useEffect(() => {
    if (showAccountDecisionMaking(brokerageFlags) && !strategy.decision_making) {
        setStrategy({ decision_making: DEFAULT_DECISION_MAKING_OPTION });
    }
  }, []);

  return (
    <div className="max-w-4xl mx-auto space-y-8 pb-10">
      <div className="text-center mb-6">
          <h2 className="text-2xl font-bold text-gray-800">Household Setup</h2>
          <p className="text-gray-600">Complete the financial profile and investment strategy.</p>
      </div>
      
      {/* STACKED Layout */}
      <div className="flex flex-col gap-8">
          
          {/* Card 1: Income Overview */}
          {showIncomeOverviewCard && (
          <CollapsibleSection
              index={0}
              title="Income Overview"
              isOpen={expandedCards[0]}
              onToggle={() => toggleCard(0)}
          >
              <div className="space-y-6">
                  {/* Tax Bracket */}
                  {showTaxBracket(brokerageFlags) && (
                  <div>
                      <label className="label-modern">Tax Bracket</label>
                      <div className="relative">
                          <select
                            className="input-modern w-full appearance-none"
                            value={formatTaxBracket(taxInfo.tax_bracket)}
                            onChange={e => handleTaxBracketChange(e.target.value)}
                          >
                              <option value="">Select...</option>
                              {TAX_BRACKET_OPTIONS.map(opt => (
                                <option key={opt} value={opt}>{opt}</option>
                              ))}
                          </select>
                          <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-gray-500">
                             <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                          </div>
                      </div>
                  </div>
                  )}

                  {/* Net Worths */}
                  {showIncomeDetails(brokerageFlags, data?.accounts) && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                          <label className="label-modern">Liquid Net Worth</label>
                          <div className="relative">
                              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                              <input 
                                type="number" 
                                min="0"
                                className="input-modern pl-7 w-full no-spinner" 
                                placeholder="0" 
                                value={data.estimated_liquid_net_worth || ''} 
                                onChange={e => handleFinancialChange('estimated_liquid_net_worth', e.target.value)} 
                                onKeyDown={(e) => e.key === '-' && e.preventDefault()}
                                onWheel={(e) => e.currentTarget.blur()}
                              />
                          </div>
                      </div>
                      <div>
                          <label className="label-modern">Total Net Worth</label>
                          <div className="relative">
                              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                              <input 
                                type="number" 
                                min="0"
                                className="input-modern pl-7 w-full no-spinner" 
                                placeholder="0" 
                                value={data.estimated_total_net_worth || ''} 
                                onChange={e => handleFinancialChange('estimated_total_net_worth', e.target.value)} 
                                onKeyDown={(e) => e.key === '-' && e.preventDefault()}
                                onWheel={(e) => e.currentTarget.blur()}
                              />
                          </div>
                      </div>
                  </div>
                  )}

                  {/* Income */}
                  {showIncomeDetails(brokerageFlags, data?.accounts) && (
                  <div>
                      <label className="label-modern">Annual Household Income</label>
                      <div className="relative">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                          <input
                            type="number"
                            min="0"
                            className="input-modern pl-7 w-full no-spinner"
                            placeholder="0"
                            value={data.annual_income || ''}
                            onChange={e => handleFinancialChange('annual_income', e.target.value)}
                            onKeyDown={(e) => e.key === '-' && e.preventDefault()}
                            onWheel={(e) => e.currentTarget.blur()}
                          />
                      </div>
                  </div>
                  )}

                  {/* Expenses — Fidelity NFS only */}
                  {(showAnnualExpenseRange(brokerageFlags) || showSpecialExpenseRange(brokerageFlags)) && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {showAnnualExpenseRange(brokerageFlags) && (
                      <RangeInput
                          label="Annual Expenses"
                          value={data.annual_expense_range}
                          onChange={val => handleFinancialChange('annual_expense_range', val)}
                      />
                      )}
                      {showSpecialExpenseRange(brokerageFlags) && (
                      <RangeInput
                          label="Special Expenses"
                          value={data.special_expense_range}
                          onChange={val => handleFinancialChange('special_expense_range', val)}
                      />
                      )}
                  </div>
                  )}
              </div>
          </CollapsibleSection>
          )}

          {/* Card 2: Regulatory Qualifiers */}
          <CollapsibleSection
              index={1 + incomeCardOffset}
              title="Regulatory Qualifiers"
              isOpen={expandedCards[1]}
              onToggle={() => toggleCard(1)}
          >
              <div className="space-y-4">
                  {[
                      { key: 'accredited_investor', label: 'Is the household considered an Accredited Investor?' },
                      { key: 'qualified_purchaser', label: 'Is the household considered a Qualified Purchaser?' },
                      { key: 'us_broker_dealer_association', label: 'Is any member of the household affiliated with a U.S. Broker-Dealer?' },
                      { key: 'broker_dealer_employee', label: 'Is any member of the household an employee of a Broker-Dealer?' },
                      { key: 'ria_association', label: 'Is any member affiliated with a Registered Investment Advisor (RIA)?' },
                      { key: 'shareholder_or_member_board_director', label: 'Is any member a 10% shareholder or board member of a public company?' },
                      { key: 'low_priced_securities', label: 'Does the household trade low-priced or restricted securities?' },
                  ].map((item) => (
                      <div key={item.key} className="flex items-center justify-between p-3 rounded-lg hover:bg-gray-50 transition-colors">
                          <span className="text-sm font-medium text-gray-700">{item.label}</span>
                          <button 
                            type="button"
                            onClick={() => handleInvestorAssociationChange(item.key)}
                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 ${associations[item.key] ? 'bg-emerald-600' : 'bg-gray-200'}`}
                          >
                              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${associations[item.key] ? 'translate-x-6' : 'translate-x-1'}`} />
                          </button>
                      </div>
                  ))}
              </div>
          </CollapsibleSection>

          {/* Card 3: Experience */}
          {experienceFieldsToRender.length > 0 && (
          <CollapsibleSection
              index={2 + incomeCardOffset}
              title="Investment Experience (Years)"
              isOpen={expandedCards[2]}
              onToggle={() => toggleCard(2)}
          >
               <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                  {experienceFieldsToRender.map((item) => (
                      <div key={item.key}>
                          <label className="label-modern text-xs uppercase text-gray-500 mb-1 block">{item.label}</label>
                          <input 
                            type="number" 
                            min="0"
                            className="input-modern w-full" 
                            placeholder="0"
                            value={experience[item.key] || ''}
                            onChange={e => handleInvestorExperienceChange(item.key, e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === '-') e.preventDefault();
                                if (e.key === 'Enter') e.currentTarget.blur();
                            }}
                            onWheel={(e) => e.currentTarget.blur()}
                          />
                      </div>
                  ))}
              </div>
          </CollapsibleSection>
          )}

          {/* Card 4: Strategy */}
          {(showStrategyDefaults(brokerageFlags, data?.accounts) ||
            showAccountDecisionMaking(brokerageFlags) ||
            showSourceOfFunds(brokerageFlags) ||
            showUseOfFunds(brokerageFlags)) && (
          <CollapsibleSection
              index={(experienceFieldsToRender.length > 0 ? 3 : 2) + incomeCardOffset}
              title="Investment Strategy"
              isOpen={expandedCards[3]}
              onToggle={() => toggleCard(3)}
          >
               <div className="mb-6 bg-blue-50 p-4 rounded-xl text-sm text-blue-800 border border-blue-100 flex gap-3">
                  <svg className="w-5 h-5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd"/></svg>
                  Selections below apply by default to all accounts. You can customize them per account in the next step.
              </div>

              <div className="space-y-6">
                  
                  {/* BFS Trigger REMOVED. Using Primary Strategy directly. */}

                   <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                       {showStrategyDefaults(brokerageFlags, data?.accounts) && (
                       <>
                       <div className="md:col-span-2">
                          <label className="label-modern">Primary Investment Objective</label>
                          <div className="relative">
                            <select 
                                className="input-modern w-full appearance-none" 
                                value={strategy.invest_objectives?.[0] || ''} 
                                onChange={e => handlePrimaryObjectiveChange(e.target.value)}
                            >
                                <option value="">Select...</option>
                                {PRIMARY_OBJECTIVE_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                            </select>
                            <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-gray-500">
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                            </div>
                          </div>
                          <p className="text-xs text-gray-500 mt-1 pl-1">Selecting an objective will auto-fill typical default values for other strategy fields (which you can then modify).</p>
                      </div>
                      
                      {/* Risk Tolerance */}
                       <div>
                          <label className="label-modern">Risk Tolerance</label>
                          <div className="relative">
                              <select 
                                className="input-modern w-full appearance-none" 
                                value={strategy.risk_tolerance || ''} 
                                onChange={e => setStrategy({ risk_tolerance: e.target.value })}
                              >
                                  <option value="">Select...</option>
                                  {RISK_TOLERANCE_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                              </select>
                              <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-gray-500">
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                            </div>
                          </div>
                      </div>
                      
                      {/* Time Horizon */}
                      <div>
                          <label className="label-modern">Time Horizon</label>
                          <div className="relative">
                              <select 
                                className="input-modern w-full appearance-none" 
                                value={formatTimeHorizon(strategy.time_horizon)} 
                                onChange={e => handleTimeHorizonChange(e.target.value)}
                              >
                                  <option value="">Select...</option>
                                  {TIME_HORIZON_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                              </select>
                              <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-gray-500">
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                            </div>
                          </div>
                      </div>
                      </>
                      )}

                      {/* Account Decision Making */}
                      {showAccountDecisionMaking(brokerageFlags) && (
                       <div className="md:col-span-2">
                          <label className="label-modern">Account Decision Making</label>
                          <div className="relative">
                              <select
                                className="input-modern w-full appearance-none"
                                value={strategy.decision_making || ''}
                                onChange={e => setStrategy({ decision_making: e.target.value })}
                              >
                                  <option value="">Select...</option>
                                  {DECISION_MAKING_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                              </select>
                              <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-gray-500">
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                            </div>
                          </div>
                      </div>
                      )}

                      {/* Source of Funds — Fidelity NFS or NewEdge */}
                      {showSourceOfFunds(brokerageFlags) && (
                      <div>
                          <label className="label-modern">Source of Funds</label>
                          <div className="relative">
                              <select
                                className="input-modern w-full appearance-none"
                                value={strategy.source_of_funds?.[0] || ''}
                                onChange={e => setStrategy({ source_of_funds: [e.target.value] })}
                              >
                                  <option value="">Select...</option>
                                  {SOURCE_OF_FUNDS_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                              </select>
                              <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-gray-500">
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                            </div>
                          </div>
                      </div>
                      )}

                      {/* Primary Use — NewEdge only */}
                      {showUseOfFunds(brokerageFlags) && (
                      <div>
                          <label className="label-modern">Use of Funds</label>
                           <div className="relative">
                              <select
                                className="input-modern w-full appearance-none"
                                value={strategy.use_of_funds || ''}
                                onChange={e => setStrategy({ use_of_funds: e.target.value })}
                              >
                                  <option value="">Select...</option>
                                  {PRIMARY_USE_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                              </select>
                              <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-gray-500">
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                            </div>
                          </div>
                      </div>
                      )}

                      {/* Liquidity Needs — NewEdge only */}
                      {showLiquidityRequirements(brokerageFlags) && (
                      <div>
                          <label className="label-modern">Liquidity Needs (Amount)</label>
                          <div className="relative">
                              <select
                                className="input-modern w-full appearance-none"
                                value={formatLiquidityNeeds(strategy.liquidity_requirements)}
                                onChange={e => handleLiquidityNeedsChange(e.target.value)}
                              >
                                  <option value="">Select...</option>
                                  {LIQUIDITY_NEEDS_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                              </select>
                              <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-gray-500">
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                            </div>
                          </div>
                      </div>
                      )}

                      {/* Liquidity Time Horizon — NewEdge only */}
                      {showLiquidityRequirements(brokerageFlags) && (
                      <div>
                          <label className="label-modern">Liquidity Time Horizon (Years)</label>
                          <div className="relative">
                              <select
                                className="input-modern w-full appearance-none"
                                value={formatLiquidityTimeHorizon(strategy.liquidity_requirements)}
                                onChange={e => handleLiquidityTimeHorizonChange(e.target.value)}
                              >
                                  <option value="">Select...</option>
                                  {LIQUIDITY_TIME_HORIZON_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                              </select>
                              <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-gray-500">
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                            </div>
                          </div>
                      </div>
                      )}

                   </div>
              </div>
          </CollapsibleSection>
          )}
      </div>

      {/* Actions */}
      <div className="flex justify-between items-center pt-8 border-t border-gray-200 sticky bottom-0 bg-white/90 backdrop-blur-sm p-4 rounded-xl shadow-lg border-t-0 -mx-4 md:mx-0">
          <button 
            onClick={onBack}
            className="text-gray-500 font-semibold hover:text-gray-700 flex items-center gap-2 px-4 py-2 rounded-lg hover:bg-gray-100 transition-colors"
          >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18"/></svg>
              Back
          </button>
          
          <button
            onClick={onNext}
            className="btn-primary px-8 py-3 rounded-xl shadow-lg hover:shadow-xl hover:scale-105 transition-all flex items-center gap-2"
          >
              Next: Account Setup
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3"/></svg>
          </button>
      </div>
    </div>
  );
};


export default Step2HouseholdSetup;
